import React, { useState } from 'react';
import { feedbackAPI } from '../services/api';
import toast from 'react-hot-toast';

const Feedback = () => {
  const [formData, setFormData] = useState({
    rating: 5,
    comment: '',
    feedback_type: 'general',
    feature_category: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await feedbackAPI.submit(formData);
      toast.success('Feedback submitted successfully! Thank you for your input.');
      setFormData({
        rating: 5,
        comment: '',
        feedback_type: 'general',
        feature_category: ''
      });
    } catch (error) {
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="max-w-4xl mx-auto py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Send Feedback</h1>
        <p className="text-gray-600">Help us improve MIND DETOX with your valuable feedback</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Feedback Form */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Rating */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Overall Rating
              </label>
              <div className="flex justify-center space-x-2 mb-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => handleInputChange('rating', star)}
                    className="text-3xl transition-colors"
                  >
                    <span className={star <= formData.rating ? 'text-yellow-400' : 'text-gray-300'}>
                      ★
                    </span>
                  </button>
                ))}
              </div>
              <div className="text-center text-sm text-gray-600">
                {formData.rating === 5 ? 'Excellent!' :
                 formData.rating === 4 ? 'Very Good' :
                 formData.rating === 3 ? 'Good' :
                 formData.rating === 2 ? 'Fair' : 'Poor'}
              </div>
            </div>

            {/* Feedback Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Feedback Type
              </label>
              <select
                value={formData.feedback_type}
                onChange={(e) => handleInputChange('feedback_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="general">General Feedback</option>
                <option value="bug_report">Bug Report</option>
                <option value="feature_request">Feature Request</option>
                <option value="complaint">Complaint</option>
              </select>
            </div>

            {/* Feature Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Feature Category (Optional)
              </label>
              <select
                value={formData.feature_category}
                onChange={(e) => handleInputChange('feature_category', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select a feature...</option>
                <option value="dashboard">Dashboard</option>
                <option value="checkin">Daily Check-in</option>
                <option value="questionnaire">Questionnaires</option>
                <option value="organ_health">Organ Health</option>
                <option value="chatbot">AI Chatbot</option>
                <option value="community">Community</option>
                <option value="reports">Reports</option>
              </select>
            </div>

            {/* Comment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Feedback
              </label>
              <textarea
                rows="6"
                value={formData.comment}
                onChange={(e) => handleInputChange('comment', e.target.value)}
                placeholder="Share your thoughts, suggestions, or report issues..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </div>
          </form>
        </div>

        {/* Contact Info */}
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Other Ways to Reach Us</h3>
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <span className="text-xl">📧</span>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Email Support</h4>
                <p className="text-gray-600">support@minddetox.com</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <span className="text-xl">💬</span>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Live Chat</h4>
                <p className="text-gray-600">Available 24/7</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <span className="text-xl">📚</span>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Help Center</h4>
                <p className="text-gray-600">FAQs and guides</p>
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-white bg-opacity-70 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Response Time</h4>
            <p className="text-sm text-gray-600">
              We typically respond to feedback within 24-48 hours. Urgent issues are prioritized.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Feedback;
